import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiClient } from '../services/api';
import AppHeader from '../components/layout/AppHeader';
import DashboardTour from '../components/onboarding/DashboardTour';
import PredictorSection from '../components/talent/PredictorSection';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [projects, setProjects] = useState<any[]>([]);
  const [rooms, setRooms] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hoursTracked, setHoursTracked] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projectsData, roomsData] = await Promise.all([
          apiClient.getProjects(),
          apiClient.getRooms()
        ]);
        // Safely extract arrays from potential object responses
        const safeProjects = Array.isArray(projectsData)
          ? projectsData
          : (projectsData?.projects || projectsData?.data || []);

        const safeRooms = Array.isArray(roomsData)
          ? roomsData
          : (roomsData?.rooms || roomsData?.data || []);

        setProjects(safeProjects);
        setRooms(safeRooms);
      } catch (err) {
        console.error('Failed to load dashboard data:', err);
        setError('Could not load dashboard data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [user]);

  // Real-time hours tracking
  useEffect(() => {
    // Initialize login time if not exists
    const initializeLoginTime = () => {
      const loginTime = localStorage.getItem('loginTime');
      if (!loginTime && user) {
        localStorage.setItem('loginTime', new Date().toISOString());
      }
    };

    initializeLoginTime();

    // Update hours tracked every second
    const interval = setInterval(() => {
      const loginTime = localStorage.getItem('loginTime');
      if (loginTime) {
        const loginDate = new Date(loginTime);
        const now = new Date();
        const diffMs = now.getTime() - loginDate.getTime();
        const hours = diffMs / (1000 * 60 * 60);
        setHoursTracked(Math.floor(hours * 10) / 10); // Round to 1 decimal
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [user]);

  // Calculate stats
  const activeProjectsCount = projects.filter(p => p.status !== 'completed').length;

  // Get recent activity from projects (simplified logic)
  const recentActivity = projects.slice(0, 3).map(p => ({
    type: 'project',
    title: `Updated ${p.name}`,
    time: 'Recently',
    desc: p.description
  }));

  if (loading) {
    return (
      <div className="min-h-screen bg-premium-wood-dark flex items-center justify-center text-premium-gold">
        <div className="animate-pulse flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-premium-gold border-t-transparent rounded-full animate-spin mb-4"></div>
          <div className="font-serif">Loading Dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-premium-wood-dark font-sans text-white overflow-hidden relative">
      {/* Tour Component */}
      <DashboardTour />

      {/* Wood Texture Background */}
      <div
        className="absolute inset-0 z-0 opacity-40 pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1543158143-4a1c0d0c3405?q=80&w=1280&q=60&auto=format&fit=crop')`,
          backgroundSize: 'cover',
          filter: 'contrast(1.1) brightness(0.7)'
        }}
      />
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-black/60 via-premium-wood-dark/80 to-black/90 pointer-events-none" />

      {/* Header */}
      <AppHeader />

      {/* Main Content */}
      <main className="relative z-10 container mx-auto px-4 pt-12 pb-24">
        {error && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8 p-4 bg-red-500/10 border border-red-500/30 text-red-200 rounded-xl flex items-center gap-3">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
            <span>{error}</span>
          </motion.div>
        )}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-7xl mx-auto">

          {/* Welcome Section */}
          <div id="dashboard-welcome" className="mb-12">
            <h1 className="font-serif text-4xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-b from-premium-gold-light via-premium-gold to-premium-gold-dark drop-shadow-lg mb-2">
              Welcome back, {user?.firstName}
            </h1>
            <p className="text-lg text-white/60">Here is your overview for today.</p>
          </div>

          {/* Stats Grid */}
          <div id="dashboard-stats" className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <motion.div whileHover={{ y: -5 }} className="bg-gradient-to-br from-white/10 to-transparent border border-white/10 p-6 rounded-2xl backdrop-blur-sm shadow-xl relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                <span className="text-6xl">📊</span>
              </div>
              <h3 className="text-white/60 font-medium mb-1">Active Projects</h3>
              <div className="text-4xl font-serif font-bold text-white">{activeProjectsCount}</div>
              <div className="text-green-400 text-xs mt-2 flex items-center">
                <span className="mr-1">↑</span> {projects.length} Total
              </div>
            </motion.div>

            <motion.div whileHover={{ y: -5 }} className="bg-gradient-to-br from-white/10 to-transparent border border-white/10 p-6 rounded-2xl backdrop-blur-sm shadow-xl relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                <span className="text-6xl">⏱️</span>
              </div>
              <h3 className="text-white/60 font-medium mb-1">Hours Tracked</h3>
              <div className="text-4xl font-serif font-bold text-premium-gold">{hoursTracked.toFixed(1)}</div>
              <div className="text-white/40 text-xs mt-2">
                Since login • Real-time
              </div>
            </motion.div>

            <motion.div whileHover={{ y: -5 }} className="bg-gradient-to-br from-white/10 to-transparent border border-white/10 p-6 rounded-2xl backdrop-blur-sm shadow-xl relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                <span className="text-6xl">👥</span>
              </div>
              <h3 className="text-white/60 font-medium mb-1">Active Rooms</h3>
              <div className="text-4xl font-serif font-bold text-white">{rooms.length}</div>
              <div className="text-white/40 text-xs mt-2">
                Collaboration spaces
              </div>
            </motion.div>

          </div>

          {/* Main Content Areas */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            {/* Quick Actions */}
            <div id="dashboard-quick-actions" className="lg:col-span-2">
              <h2 className="text-2xl font-serif font-bold text-premium-gold mb-6 flex items-center gap-2">
                <span className="w-8 h-[1px] bg-premium-gold/50"></span> Quick Actions
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { label: 'New Project', icon: '🚀', path: '/projects' },
                  { label: 'Find Talent', icon: '🔍', path: '/talent' },
                  { label: 'Join Meeting', icon: '🎥', path: '/workspace' },
                  { label: '3D Hub', icon: '🧊', path: '/3dhub' },
                  { label: 'Skills', icon: '⚡', path: '/skills' },
                  { label: 'Academy', icon: '🎓', path: '/academy' },
                  { label: 'AI Hub', icon: '🤖', path: '/unitybot' },
                  { label: 'My Vault', icon: '💰', path: '/financial' },
                ].map((action, i) => (
                  <motion.button
                    key={i}
                    id={`dashboard-action-${action.label.replace(/\s+/g, '-').toLowerCase()}`}
                    whileHover={{ scale: 1.02, backgroundColor: 'rgba(255,255,255,0.1)' }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigate(action.path)}
                    className="p-4 sm:p-6 bg-white/5 border border-white/10 rounded-xl flex flex-col items-center justify-center gap-3 transition-colors group"
                  >
                    <span className="text-3xl group-hover:scale-110 transition-transform">{action.icon}</span>
                    <span className="font-medium text-white/80 group-hover:text-white">{action.label}</span>
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Recent Activity Feed */}
            <div id="dashboard-activity" className="bg-black/20 border border-white/10 rounded-xl p-6 backdrop-blur-sm">
              <h2 className="text-xl font-serif font-bold text-white mb-6">Recent Activity</h2>
              <div className="space-y-6">
                {recentActivity.length > 0 ? (
                  recentActivity.map((activity, i) => (
                    <div key={i} className="flex gap-4 group">
                      <div className="flex flex-col items-center">
                        <div className="w-2 h-2 rounded-full bg-premium-gold mb-1 group-hover:scale-125 transition-transform"></div>
                        <div className="w-[1px] h-full bg-white/10 group-last:hidden"></div>
                      </div>
                      <div>
                        <h4 className="font-bold text-sm text-white">{activity.title}</h4>
                        <p className="text-xs text-white/50 mb-1">{activity.time}</p>
                        <p className="text-sm text-white/70">{activity.desc || 'No details provided.'}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-white/40 py-8">
                    No recent activity found.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* AI Predictor Section */}
          <div className="mb-12">
            <PredictorSection />
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default Dashboard;
